/**
 * The design tokens, as CSS custom properties.
 *
 * One file, because D-01 makes this product generic and whitelabelled with Primerica as customer
 * one. A retheme has to be somebody overriding a short list of variables, not editing components,
 * so every colour, radius and font decision a component makes reads from here and nothing hardcodes
 * a hex value below this line. `surface.spec.ts` enforces both halves of that.
 *
 * The palette is warm rather than neutral-cool: a paper ground, ink that is nearly black but not
 * quite, and lines that are a warm grey. The accent is a deep teal-green. It replaces the earlier
 * placeholder green, which `tokens.ts` used to justify by quoting figure 5's "no branding,
 * deliberately, because branding at this stage invites colour opinions instead of architecture
 * discussion". That reasoning expired when the architecture review happened; this is the reversal,
 * made knowingly.
 *
 * ## Three kinds of colour, kept apart
 *
 * **Brand** is the three a customer changes first. **Ground and ink** are the neutrals, biased very
 * slightly toward the accent so they read as chosen rather than as a default grey. **Status** is
 * separate from the accent on purpose: a customer whose brand colour happens to be red must not
 * thereby make every success message look like a failure.
 *
 * ## Light and dark, and the override
 *
 * `prefers-color-scheme` handles the device. The `[data-theme]` blocks after it handle a person who
 * has overridden the device, and they come last so they win in both directions - a media query cannot
 * beat them, which is what makes "force light on a dark phone" work rather than half-work. `theme.ts`
 * is the only thing that writes that attribute.
 *
 * Dark is a retune rather than an inversion. The accent lifts to stay legible on a dark ground, the
 * neutrals stay warm, and every token declared in light is declared again - a token that exists in one
 * block and not the other renders as nothing at all, which is an invisible control rather than an
 * error.
 *
 * A string rather than a `.css` file for the reason `styles.ts` gives: this package is consumed as
 * source with no build step, so it must make no bundler assumptions. The apps inject it.
 *
 * ## Overriding
 *
 * A customer stylesheet redefines any of these on `:root` after this block. Nothing here is
 * `!important` and nothing is scoped tighter than `:root` or `:root[data-theme=...]`, which is what
 * makes that work.
 */
export const tokens = `
  :root {
    color-scheme: light dark;

    /* Brand. The three a customer changes first. */
    --ddds-accent: #0C6B5E;
    --ddds-accent-hover: #095548;
    --ddds-accent-ink: #FFFFFF;
    --ddds-accent-quiet: #E7F1EE;

    /* Grounds and ink. Warm neutrals, biased toward the accent. */
    --ddds-bg: #F6F4EF;
    --ddds-rail: #FBFAF7;
    --ddds-surface: #FFFFFF;
    --ddds-sunken: #F3EFE6;
    --ddds-ink: #1D2724;
    --ddds-ink-quiet: #6D7773;
    --ddds-ink-faint: #98A19C;
    --ddds-line: #E8E4DB;
    --ddds-line-strong: #DDD7CA;

    /* Status. Deliberately not the accent; see the file comment. */
    --ddds-ok: #2F7D51;
    --ddds-ok-quiet: #E4F2E8;
    --ddds-warn-ink: #9A6A12;
    --ddds-warn-quiet: #F8EED7;
    --ddds-warn-line: #D9962B;
    --ddds-error-ink: #B2453D;
    --ddds-error-quiet: #F9E8E5;
    --ddds-info-ink: #2F5E9E;
    --ddds-info-quiet: #E3ECF8;

    /*
     * Type. Two faces, self-hosted; see fonts.ts for why they are not linked.
     * The fallbacks are a system stack rather than a generic, so a surface whose
     * fonts have not arrived yet is merely plainer and never unstyled.
     */
    --ddds-font-display: 'Bricolage Grotesque', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
    --ddds-font: 'Albert Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
    --ddds-font-mono: ui-monospace, SFMono-Regular, Menlo, monospace;

    /* Shape and rhythm. */
    --ddds-radius: 12px;
    --ddds-radius-sm: 8px;
    --ddds-radius-pill: 99px;
    --ddds-gap: 1rem;
    --ddds-gap-lg: 1.75rem;

    /*
     * The smallest a tappable thing may be. 44px is Apple's guideline and the reason
     * this surface is built mobile first: the dominant action is a person photographing
     * a licence on a phone, one-handed, possibly outdoors.
     */
    --ddds-touch: 44px;

    --ddds-shadow: 0 1px 2px rgb(29 39 36 / 5%), 0 4px 14px rgb(29 39 36 / 4%);
    /* Behind an overlay dialog. A token rather than a literal because D-01 makes a retheme one file,
       and because the two palettes need different ones: the light scrim is the ink colour at low
       alpha, and on a dark ground that is invisible, so dark goes darker than its own background. */
    --ddds-scrim: rgb(29 39 36 / 42%);
  }

  @media (prefers-color-scheme: dark) {
    :root {
      --ddds-accent: #5FBFA4;
      --ddds-accent-hover: #7BD0B7;
      --ddds-accent-ink: #08211A;
      --ddds-accent-quiet: #17302A;

      --ddds-bg: #141614;
      --ddds-rail: #191C19;
      --ddds-surface: #1E221F;
      --ddds-sunken: #23271F;
      --ddds-ink: #EAE7E0;
      --ddds-ink-quiet: #A3A99F;
      --ddds-ink-faint: #7E857B;
      --ddds-line: #2C302B;
      --ddds-line-strong: #3A3F38;

      --ddds-ok: #74C894;
      --ddds-ok-quiet: #1A2E21;
      --ddds-warn-ink: #E5BE74;
      --ddds-warn-quiet: #2E2614;
      --ddds-warn-line: #C08A3E;
      --ddds-error-ink: #EF9D94;
      --ddds-error-quiet: #331D1A;
      --ddds-info-ink: #9DBDE9;
      --ddds-info-quiet: #182231;

      --ddds-shadow: none;
      --ddds-scrim: rgb(4 6 5 / 62%);
    }
  }

  /*
   * A person's override, which has to beat the media query in both directions.
   *
   * These come last and are one specificity point heavier, so forcing light on a device set to dark
   * works as well as the other way round - the case a naive implementation gets wrong, because it
   * only ever tests forcing dark on a light machine.
   */
  :root[data-theme="dark"] {
    color-scheme: dark;

    --ddds-accent: #5FBFA4;
    --ddds-accent-hover: #7BD0B7;
    --ddds-accent-ink: #08211A;
    --ddds-accent-quiet: #17302A;

    --ddds-bg: #141614;
    --ddds-rail: #191C19;
    --ddds-surface: #1E221F;
    --ddds-sunken: #23271F;
    --ddds-ink: #EAE7E0;
    --ddds-ink-quiet: #A3A99F;
    --ddds-ink-faint: #7E857B;
    --ddds-line: #2C302B;
    --ddds-line-strong: #3A3F38;

    --ddds-ok: #74C894;
    --ddds-ok-quiet: #1A2E21;
    --ddds-warn-ink: #E5BE74;
    --ddds-warn-quiet: #2E2614;
    --ddds-warn-line: #C08A3E;
    --ddds-error-ink: #EF9D94;
    --ddds-error-quiet: #331D1A;
    --ddds-info-ink: #9DBDE9;
    --ddds-info-quiet: #182231;

    --ddds-shadow: none;
    --ddds-scrim: rgb(4 6 5 / 62%);
  }

  :root[data-theme="light"] {
    color-scheme: light;

    --ddds-accent: #0C6B5E;
    --ddds-accent-hover: #095548;
    --ddds-accent-ink: #FFFFFF;
    --ddds-accent-quiet: #E7F1EE;

    --ddds-bg: #F6F4EF;
    --ddds-rail: #FBFAF7;
    --ddds-surface: #FFFFFF;
    --ddds-sunken: #F3EFE6;
    --ddds-ink: #1D2724;
    --ddds-ink-quiet: #6D7773;
    --ddds-ink-faint: #98A19C;
    --ddds-line: #E8E4DB;
    --ddds-line-strong: #DDD7CA;

    --ddds-ok: #2F7D51;
    --ddds-ok-quiet: #E4F2E8;
    --ddds-warn-ink: #9A6A12;
    --ddds-warn-quiet: #F8EED7;
    --ddds-warn-line: #D9962B;
    --ddds-error-ink: #B2453D;
    --ddds-error-quiet: #F9E8E5;
    --ddds-info-ink: #2F5E9E;
    --ddds-info-quiet: #E3ECF8;

    --ddds-shadow: 0 1px 2px rgb(29 39 36 / 5%), 0 4px 14px rgb(29 39 36 / 4%);
    --ddds-scrim: rgb(29 39 36 / 42%);
  }
`;
